import React, { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { PiRocketDuotone } from "react-icons/pi"
import api from "../services/api.service"
import { APP_VERSION } from "../appVersion"
import "./NewVersionPill.scss"

/**
 * "Update Required" pill, top right over every screen. On the app/version
 * socket a tab tells the API the release it was built with and gets back the
 * live one, on connect and whenever it changes. A tab on a different release
 * shows the pill; a click reloads it onto the live build, whose number
 * matches, so it stays hidden from then on.
 *
 * It can be dragged out of the way (kept on screen); where it was left is not
 * remembered.
 *
 * @param enabled {Boolean} listen (signed in only: the socket needs a token)
 */
const NewVersionPill = ({ enabled }) => {
    const [open, setOpen] = useState(false)
    const boundsRef = useRef()
    // A drag ends with a click on the pill: that one must not reload
    const dragged = useRef(false)

    useEffect(() => {
        if (!enabled) return undefined

        let socket
        let stopped = false

        api.socket
            .connect("app/version", { query: { version: APP_VERSION } })
            .then((s) => {
                socket = s

                if (stopped) {
                    socket.disconnect()
                    return
                }

                socket.on("version", (data) => {
                    const version = Number(data?.version)

                    if (version > 0) {
                        setOpen(version !== APP_VERSION)
                    }
                })
            })
            .catch(() => {})

        return () => {
            stopped = true
            socket?.disconnect()
        }
    }, [enabled])

    return (
        <>
            {open && <div ref={boundsRef} className="x-new-version-bounds" />}
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="x-new-version"
                        className="x-new-version"
                        initial={{ y: -40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -40, opacity: 0, transition: { duration: 0.2 } }}
                        transition={{ type: "spring", stiffness: 420, damping: 16, delay: 2 }}
                        drag
                        dragConstraints={boundsRef}
                        dragElastic={0.1}
                        dragMomentum={false}
                        onPointerDown={() => {
                            dragged.current = false
                        }}
                        onDragStart={() => {
                            dragged.current = true
                        }}
                    >
                        <div
                            className="x-new-version-pill"
                            onClick={() => {
                                if (!dragged.current) window.location.reload()
                            }}
                        >
                            <PiRocketDuotone /> Update Required
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}

export default NewVersionPill
